import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Col, Form, Row, Select, Tag } from "antd";
import { useCallback, useEffect, useState } from "react";
import { ContentBlockerField, ERcbTier, EUserRole, TRANSLATABLE_CONTENT_BLOCKER_TEMPLATE_ATTRIBUTES_LIST, TemplateMetaDataField, getTemplateHeadline } from "@devowl-wp/api-real-cookie-banner";
import { useServiceTemplateMetaData } from "../../../../../../hooks/service/useServiceTemplateMetaData.js";
import { useTranslation } from "../../../../../../others/i18n.js";
import { useStores } from "../../../../../../store/root.js";
import { service } from "../../../../../../styles/service.js";
import { spacing } from "../../../../../../styles/spacing.js";
import { getDisableStrategies } from "../../../../../../util/template/merge-strategies.js";
import { getFieldNameTranslated } from "../../../../../../util/template/translation.js";
import { FormFieldVisibilityWrapper } from "../../formFieldVisibilityWrapper.js";
import { TemplateMetaDataExtendsStrategyFormField } from "../../templateMetaDataExtendsStrategyFormField.js";
function FormFieldContentBlockerTemplateConnectedServices() {
    const { t } = useTranslation();
    const { rcbAuthenticationStore } = useStores();
    const hasCreatorRole = rcbAuthenticationStore.hasRole(EUserRole.Creator);
    // consider if content blocker is free or pro -> filter templates available
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const serviceQuery = useServiceTemplateMetaData(true, true);
    const mapTemplateToOption = useCallback((serviceMeta)=>{
        return {
            label: getTemplateHeadline(serviceMeta),
            value: serviceMeta.identifier
        };
    }, []);
    const getOptionsCount = (options)=>{
        if (options && options.length > 0) {
            return options.map((option)=>option?.options?.length)?.reduce((sum, value)=>sum + value);
        }
        return 0;
    };
    useEffect(()=>{
        const { isFetching, isLoading, isFetched, isSuccess, data } = serviceQuery;
        if (isFetched && isSuccess && data && getOptionsCount(options || []) !== data.length) {
            setOptions([
                {
                    label: /*#__PURE__*/ _jsx(Tag, {
                        color: service.tier.free.color,
                        children: t("FREE")
                    }),
                    options: data.filter((t)=>t.tier === ERcbTier.Free).map((template)=>mapTemplateToOption(template)).sort((a, b)=>a.label.localeCompare(b.label))
                },
                {
                    label: /*#__PURE__*/ _jsx(Tag, {
                        color: service.tier.pro.color,
                        children: t("PRO")
                    }),
                    options: data.filter((t)=>t.tier === ERcbTier.Pro).map((template)=>mapTemplateToOption(template)).sort((a, b)=>a.label.localeCompare(b.label))
                }
            ]);
        }
        setLoading(isFetching || isLoading);
    }, [
        serviceQuery,
        options,
        loading
    ]);
    return /*#__PURE__*/ _jsx(FormFieldVisibilityWrapper, {
        fieldNames: ContentBlockerField.ServiceTemplateIdentifiers,
        permissionCheck: {
            whenRole: EUserRole.Translator,
            roleWhiteList: TRANSLATABLE_CONTENT_BLOCKER_TEMPLATE_ATTRIBUTES_LIST
        },
        children: /*#__PURE__*/ _jsx(Form.Item, {
            label: getFieldNameTranslated(ContentBlockerField.ServiceTemplateIdentifiers, t),
            extra: t("A content blocker is displayed until the user has agreed to all necessary services that would be used by loading the content. You must define all services that are loaded as soon as the user wants to see the blocked content."),
            shouldUpdate: (prev, next)=>prev.extends?.identifier !== next.extends?.identifier,
            required: true,
            children: ({ getFieldValue })=>{
                const extended = getFieldValue(TemplateMetaDataField.Extends);
                const extendedConnectedServices = extended?.[ContentBlockerField.ServiceTemplateIdentifiers];
                return /*#__PURE__*/ _jsx(_Fragment, {
                    children: /*#__PURE__*/ _jsxs(Row, {
                        children: [
                            /*#__PURE__*/ _jsxs(Col, {
                                flex: "auto",
                                children: [
                                    " ",
                                    /*#__PURE__*/ _jsx(TemplateMetaDataExtendsStrategyFormField, {
                                        extendsFieldName: ContentBlockerField.ServiceTemplateIdentifiers,
                                        disableStrategies: getDisableStrategies(true),
                                        onKeepContent: extended && /*#__PURE__*/ _jsx(Select, {
                                            disabled: true,
                                            defaultValue: extendedConnectedServices,
                                            mode: "multiple",
                                            options: options
                                        }),
                                        children: /*#__PURE__*/ _jsx(Form.Item, {
                                            name: ContentBlockerField.ServiceTemplateIdentifiers,
                                            noStyle: true,
                                            rules: [
                                                {
                                                    required: hasCreatorRole,
                                                    message: t("Please choose a minimum of one connected service.")
                                                }
                                            ],
                                            children: /*#__PURE__*/ _jsx(Select, {
                                                loading: loading,
                                                mode: "multiple",
                                                allowClear: true,
                                                options: options
                                            })
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(Col, {
                                flex: "100px",
                                style: {
                                    marginLeft: spacing.layout.content.marginSmall
                                },
                                children: [
                                    " ",
                                    /*#__PURE__*/ _jsx(Button, {
                                        children: /*#__PURE__*/ _jsx("a", {
                                            href: "/rcb/service/create",
                                            target: "_blank",
                                            children: t("Create new service")
                                        })
                                    })
                                ]
                            })
                        ]
                    })
                });
            }
        })
    });
}
export { FormFieldContentBlockerTemplateConnectedServices };
